import { z } from 'zod';
import { Id, IsoDate } from './common';
import { YearMonth } from './costs';
import { OpportunityRow } from './opportunities';
import { ScanRow } from './scans';
import { ExperimentStatus } from './transitions';

/**
 * Tableau de bord (`api.md` § Tableau de bord et calibration, fiche T29) : ce qui attend une
 * décision sur la fenêtre courante (7 jours par défaut, `?days=`). Toutes les listes sont filtrées
 * par `marketId` sauf le budget, qui est mensuel et global.
 */

export const DASHBOARD_DAYS_DEFAULT = 7;
export const DASHBOARD_DAYS_MAX = 365;
/** Top des opportunités à trier affiché sur le tableau de bord. */
export const DASHBOARD_TO_TRIAGE_TOP = 5;
/** Shortlist affichée sur le tableau de bord. */
export const DASHBOARD_SHORTLIST_ITEMS = 10;
/** Derniers scans affichés sur le tableau de bord. */
export const DASHBOARD_LAST_SCANS = 5;

/** `?marketId=` (défaut : tous), `?days=` (défaut 7). */
export const DashboardQuery = z.object({
  marketId: Id.optional(),
  days: z.coerce.number().int().min(1).max(DASHBOARD_DAYS_MAX).default(DASHBOARD_DAYS_DEFAULT),
});
export type DashboardQuery = z.infer<typeof DashboardQuery>;
export type DashboardQueryInput = z.input<typeof DashboardQuery>;

/** Expérience `planned` ou `running`, avec son opportunité et le décompte des jours restants. */
export const DashboardExperiment = z.object({
  id: Id,
  name: z.string(),
  opportunityId: Id,
  opportunityTitle: z.string(),
  status: ExperimentStatus,
  budgetPlannedUsd: z.number(),
  budgetSpentUsd: z.number().nullable(),
  /** `YYYY-MM-DD` (colonne `date`). */
  plannedEnd: z.string().nullable(),
  /** Jours jusqu'à `plannedEnd` ; négatif quand la date est dépassée. */
  daysLeft: z.number().int().nullable(),
  ownerName: z.string().nullable(),
});
export type DashboardExperiment = z.infer<typeof DashboardExperiment>;

/** Budget du mois courant (`cost_ledger` + `settings.budget`), jamais filtré par marché. */
export const DashboardBudget = z.object({
  month: YearMonth,
  spentUsd: z.number(),
  capUsd: z.number(),
  warnRatio: z.number(),
  /** `spentUsd / capUsd` (0 si le plafond est nul). */
  ratio: z.number(),
});
export type DashboardBudget = z.infer<typeof DashboardBudget>;

/** Activité de la période `[since, now]`. */
export const DashboardStats = z.object({
  opportunitiesNew: z.number().int(),
  opportunitiesRescored: z.number().int(),
  /** Changements de statut décidés par un humain (événements `status_changed` avec acteur). */
  decisionsTaken: z.number().int(),
  experimentsCompleted: z.number().int(),
  scansCompleted: z.number().int(),
});
export type DashboardStats = z.infer<typeof DashboardStats>;

export const DashboardResponse = z.object({
  /** Début de la fenêtre (`now − days`). */
  since: IsoDate,
  days: z.number().int(),
  toTriage: z.object({
    count: z.number().int(),
    top: z.array(OpportunityRow).max(DASHBOARD_TO_TRIAGE_TOP),
  }),
  shortlisted: z.object({
    count: z.number().int(),
    items: z.array(OpportunityRow).max(DASHBOARD_SHORTLIST_ITEMS),
  }),
  experimentsRunning: z.array(DashboardExperiment),
  lastScans: z.array(ScanRow).max(DASHBOARD_LAST_SCANS),
  budget: DashboardBudget,
  stats: DashboardStats,
});
export type DashboardResponse = z.infer<typeof DashboardResponse>;
